// src/components/Cart/CartSheet.js
/**
 * CartSheet — Mobile bottom sheet cart (mobile only, hidden on desktop).
 *
 * Renders via React portal to document.body to avoid z-index stacking context issues.
 *
 * Animation: framer-motion spring animation, rises from y:"100%" to y:"50vh".
 * Drag: useDragControls so only DragHandleRow initiates drag (prevents scroll vs drag
 * conflict on mobile — RESEARCH.md Pitfall 3). drag="y" only, dragConstraints top=0.
 * Dismiss: swipe-down past 80px threshold (D-05/D-06) or X button.
 * Scroll lock: body overflow hidden while sheet is open.
 * Accessibility: role="dialog", aria-modal="true", descriptive aria-labels on all controls.
 */

import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useDragControls, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import { useCart } from "../../context/CartContext";
import { useCartDrawer } from "../../context/CartDrawerContext";
import { useCartRecommendations } from "../../hooks/useCartRecommendations";
import * as S from "./CartSheet.styled";
import { formatLek } from "../../Utils/currency";
import { readAvailability } from "../../Utils/availability";

// How far the user must drag down to dismiss the sheet (D-05, D-06).
const DISMISS_THRESHOLD = 80;
const EXPAND_THRESHOLD = 40;
const SHEET_TRANSITION = { type: "spring", damping: 30, stiffness: 300 };
const SHEET_EXIT_TRANSITION = { type: "spring", damping: 24, stiffness: 420, mass: 0.75 };

// ── Component ────────────────────────────────────────────────────────────────
const CartSheet = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOpen, isPeeking, openDrawer, closeDrawer } = useCartDrawer();
  const {
    cartItems,
    addItemToCart,
    removeItemFromCart,
    updateItemQuantity,
  } = useCart();
  const { recs, loading: recsLoading } = useCartRecommendations();
  const dragControls = useDragControls();
  const panelRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const [isMobileViewport, setIsMobileViewport] = React.useState(() =>
    typeof window === "undefined" || !window.matchMedia
      ? true
      : window.matchMedia("(max-width: 768px)").matches
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const media = window.matchMedia("(max-width: 768px)");
    const handleChange = (event) => setIsMobileViewport(event.matches);

    if (media.addEventListener) media.addEventListener("change", handleChange);
    else media.addListener(handleChange);

    setIsMobileViewport(media.matches);

    return () => {
      if (media.removeEventListener) media.removeEventListener("change", handleChange);
      else media.removeListener(handleChange);
    };
  }, []);

  const show = (isOpen || isPeeking) && isMobileViewport;

  useEffect(() => {
    if (!show || !isOpen) return undefined;
    const panel = panelRef.current;
    if (!panel) return undefined;
    const previousFocus = document.activeElement;
    const controls = () => Array.from(panel.querySelectorAll(
      'button:not(:disabled), a[href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]'
    )).filter((node) => !node.closest('[hidden], [aria-hidden="true"]'));
    (controls()[0] || panel).focus({ preventScroll: true });
    const handleKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDrawer();
      } else if (event.key === "Tab") {
        const elements = controls();
        const first = elements[0] || panel;
        const last = elements[elements.length - 1] || panel;
        if (!panel.contains(document.activeElement) || document.activeElement === panel ||
          (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [show, isOpen, closeDrawer]);

  // Body scroll lock — prevents page content from scrolling behind the sheet.
  useEffect(() => {
    if (!show) return;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [show]);

  // Dismiss sheet when drag velocity/offset exceeds threshold (D-05, D-06).
  const handleDragEnd = (_, info) => {
    if (info.offset.y > DISMISS_THRESHOLD || info.velocity.y > 650) {
      closeDrawer();
      return;
    }

    if (isPeeking && info.offset.y < -EXPAND_THRESHOLD) {
      openDrawer();
    }
  };

  const handleDragStart = (event) => {
    if (event.target.closest("[data-cart-sheet-no-drag='true']")) {
      return;
    }
    dragControls.start(event);
  };

  const handleBackdropDismiss = () => {
    closeDrawer();
  };

  // Derived subtotal from cart items.
  const totalPrice = cartItems.reduce(
    (total, item) =>
      total + Number(item.price) * item.quantity,
    0
  );

  const handleContinueShopping = () => {
    closeDrawer();
    setTimeout(() => navigate("/baskets"), 0);
  };

  const handleCheckout = () => {
    closeDrawer();
    navigate("/checkout");
  };

  const handleAddRec = (rec) => {
    addItemToCart({
      id: rec.id,
      farmId: rec.farmId,
      name: rec.name,
      price: Number(rec.price),
      image: rec.image,
      availableQuantity: rec.availableQuantity ?? null,
      quantity: 1,
    });
  };

  const handleRecKeyDown = (event, rec) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleAddRec(rec);
    }
  };

  return createPortal(
    <AnimatePresence>
      {show && (
        <>
          {/* Transparent backdrop — mobile outside tap dismisses the sheet. */}
          <S.SheetBackdrop
            data-testid="cart-sheet-backdrop"
            onClick={handleBackdropDismiss}
          />

          {/* Sheet panel — drag="y" only, dragConstraints.top=0 prevents drag above viewport */}
          <motion.div
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={t("cart.drawer_label", "Shopping cart")}
            drag="y"
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.18}
            dragListener={false}
            dragSnapToOrigin
            onDragEnd={handleDragEnd}
            initial={reduceMotion ? false : { y: "100%" }}
            animate={{ y: isPeeking && !isOpen ? "calc(100% - 11rem)" : 0 }}
            exit={{ y: "100%", transition: reduceMotion ? { duration: 0 } : SHEET_EXIT_TRANSITION }}
            transition={reduceMotion ? { duration: 0 } : SHEET_TRANSITION}
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1300,
            }}
          >
            <S.SheetPanel>
              {/* ── Drag handle ── */}
              {/* onPointerDown delegates drag control to dragControls so only this
                  element initiates drag — prevents conflict with scrollable item list
                  (RESEARCH.md Pitfall 3). touch-action: none is set on DragHandleRow. */}
              <S.DragHandleRow
                onPointerDown={handleDragStart}
                aria-label={t("cart.drag_handle", "Drag to resize cart")}
              >
                <S.DragHandle />
              </S.DragHandleRow>

              {/* ── Header ── */}
              <S.SheetHeader onPointerDown={handleDragStart}>
                <S.SheetTitle>{t("cart.page_title", "Cart")}</S.SheetTitle>
                <S.SheetCloseButton
                  data-cart-sheet-no-drag="true"
                  type="button"
                  onClick={closeDrawer}
                  onPointerDown={(event) => event.stopPropagation()}
                  aria-label={t("cart.close", "Close cart")}
                >
                  <FaTimes aria-hidden="true" />
                </S.SheetCloseButton>
              </S.SheetHeader>

              {/* ── Content: empty state or item list ── */}
              {cartItems.length === 0 ? (
                <S.SheetEmptyWrap>
                  <S.SheetEmptyHeading>
                    {t("cart.empty_heading", "Your cart is empty")}
                  </S.SheetEmptyHeading>
                  <S.SheetEmptyBody>
                    {t(
                      "cart.empty_body",
                      "Looks like you haven't added anything yet."
                    )}
                  </S.SheetEmptyBody>
                  <S.SheetCheckoutBtn
                    variant="primary"
                    size="md"
                    onClick={handleContinueShopping}
                  >
                    {t("cart.continue_shopping", "Continue Shopping")}
                  </S.SheetCheckoutBtn>
                </S.SheetEmptyWrap>
              ) : (
                <>
                  {/* ── Item list ── */}
                  <S.SheetItemList>
                    {cartItems.map((item) => {
                      const available = readAvailability(item);
                      const atCap =
                        available !== null && item.quantity >= available;
                      return (
                      <S.ItemRow key={item.id}>
                        <S.ItemThumb
                          src={item.image}
                          alt={item.name}
                          loading="lazy"
                        />
                        <S.ItemInfo>
                          <S.ItemName>{item.name}</S.ItemName>
                          <S.QtyControls>
                            <S.QtyBtn
                              type="button"
                              disabled={item.quantity <= 1}
                              onClick={() =>
                                updateItemQuantity(item.id, item.quantity - 1)
                              }
                              aria-label={t(
                                "cart.decrease_of",
                                "Decrease quantity of {{name}}",
                                { name: item.name }
                              )}
                            >
                              -
                            </S.QtyBtn>
                            <S.QtyValue>{item.quantity}</S.QtyValue>
                            <S.QtyBtn
                              type="button"
                              disabled={atCap}
                              onClick={() =>
                                updateItemQuantity(item.id, item.quantity + 1)
                              }
                              aria-label={t(
                                "cart.increase_of",
                                "Increase quantity of {{name}}",
                                { name: item.name }
                              )}
                            >
                              +
                            </S.QtyBtn>
                          </S.QtyControls>
                        </S.ItemInfo>
                        <S.ItemPrice>
                          {formatLek(Number(item.price) * item.quantity)}
                        </S.ItemPrice>
                        <S.RemoveBtn
                          type="button"
                          onClick={() => removeItemFromCart(item.id)}
                          aria-label={t(
                            "cart.remove_item",
                            "Remove {{name}} from cart",
                            { name: item.name }
                          )}
                        >
                          <FaTimes aria-hidden="true" />
                        </S.RemoveBtn>
                      </S.ItemRow>
                      );
                    })}
                  </S.SheetItemList>

                  {/* ── Recommendations placeholder (CART-05, Phase 20) ── */}
                  {(recsLoading || recs.length > 0) && (
                    <S.RecsSection>
                      <S.RecsHeading>
                        {t("cart.recs_heading", "You might also like")}
                      </S.RecsHeading>
                      <S.RecsGrid>
                        {recsLoading
                          ? [0, 1, 2].map((index) => (
                              <S.RecsSkeleton key={index} aria-busy="true" />
                            ))
                          : recs.map((rec) => (
                              <S.RecCard
                                key={rec.id}
                                role="button"
                                tabIndex={0}
                                aria-label={t(
                                  "cart.add_rec",
                                  "Add {{name}}",
                                  { name: rec.name }
                                )}
                                onClick={() => handleAddRec(rec)}
                                onKeyDown={(event) =>
                                  handleRecKeyDown(event, rec)
                                }
                              >
                                <S.RecThumb
                                  src={rec.image}
                                  alt={rec.name}
                                  loading="lazy"
                                />
                                <S.RecInfo>
                                  <S.RecName>{rec.name}</S.RecName>
                                  <S.RecPrice>
                                    {formatLek(rec.price)}
                                  </S.RecPrice>
                                </S.RecInfo>
                                <S.RecAddBtn
                                  type="button"
                                  aria-label={t(
                                    "cart.add_rec",
                                    "Add {{name}}",
                                    { name: rec.name }
                                  )}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleAddRec(rec);
                                  }}
                                >
                                  {t("cart.add", "Add")}
                                </S.RecAddBtn>
                              </S.RecCard>
                            ))}
                      </S.RecsGrid>
                    </S.RecsSection>
                  )}

                  {/* ── Footer ── */}
                  <S.SheetFooter>
                    <S.SubtotalRow>
                      <S.SubtotalLabel>
                        {t("cart.subtotal", "Subtotal")}
                      </S.SubtotalLabel>
                      <S.SubtotalValue>{formatLek(totalPrice)}</S.SubtotalValue>
                    </S.SubtotalRow>
                    <S.SubtotalNote>
                      {t(
                        "cart.delivery_note",
                        "(Delivery calculated at checkout)"
                      )}
                    </S.SubtotalNote>
                    <S.SheetCheckoutBtn
                      variant="primary"
                      size="md"
                      onClick={handleCheckout}
                    >
                      {t("cart.proceed_checkout", "Proceed to Checkout")}
                    </S.SheetCheckoutBtn>
                  </S.SheetFooter>
                </>
              )}
            </S.SheetPanel>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CartSheet;
